/**
 * Payroll Calculations Utility
 *
 * This is the SINGLE SOURCE OF TRUTH for all payroll calculations.
 * All components should use these functions instead of accessing employee.totals directly.
 *
 * Note: The `totals` object is stripped from employee data before saving to Firestore,
 * so we must calculate from raw `earnings` and `deductions` fields.
 *
 * Government deductions (sss, philhealth, pagibig, tax) support per-month structure
 * for cross-month payroll periods: { '2025-12': 300, '2026-01': 300 }
 *
 * PRD_PayrollFigureAlignment — what the words mean, and where:
 *
 * C1  Gross Pay is STATUTORY EARNINGS ONLY: (basic + OT + ND + holiday + rest day)
 *     − (undertime + absence). Manual adjustments are never inside it, on any
 *     surface, so an employee can verify Gross from their own DTR.
 * C2  "Total Deductions" meaning EVERYTHING WITHHELD (government + loans + manual
 *     deductions) is `totalWithheld`, and it is for the INDIVIDUAL PAYSLIP ONLY.
 *     The Summary, Detail and payroll-list surfaces render Loans and Adjustments as
 *     their own figures beside the government total, so substituting it there would
 *     subtract both twice. (PayslipList shows neither and so does not foot — a
 *     pre-existing gap this PRD does not change.)
 * C3  `totalDeductions` stays GOVERNMENT-ONLY and keeps its name. It is persisted
 *     per run (`summary.totalDeductions`) and is a CSV column id in customers'
 *     saved templates; redefining either would restate runs already paid. Other
 *     surfaces are RELABELLED to "Government Contributions", not redefined.
 * C4  The asymmetry is deliberate: manual EARNINGS sit outside Gross (their own
 *     "Additional Earnings" block); manual DEDUCTIONS sit inside `totalWithheld`.
 *     Gross answers "what did the work earn"; withheld answers "what was taken".
 * C7  The `net` expression below is NOT refactored in terms of `totalWithheld`.
 *     The two are algebraically identical, which is exactly why rewriting it is
 *     the cheapest way to break a Net figure by accident.
 */

/**
 * Round a number to 2 decimal places to avoid floating-point precision errors.
 * Essential for currency calculations where 1709.9999999998 should display as 1710.00.
 *
 * @param {number} value - The value to round
 * @returns {number} Value rounded to 2 decimal places
 */
function roundCurrency(value) {
  return Math.round(value * 100) / 100;
}

/**
 * Helper to sum a deduction value that may be either:
 * - A flat number (legacy or non-government deductions)
 * - A per-month object: { '2025-12': 300, '2026-01': 300 }
 *
 * @param {number|Object} deduction - Deduction value (number or per-month object)
 * @returns {number} Total deduction amount
 */
function sumDeduction(deduction) {
  if (typeof deduction === 'object' && deduction !== null && !Array.isArray(deduction)) {
    return Object.values(deduction).reduce((sum, val) => sum + (val || 0), 0);
  }
  return deduction || 0;
}

/**
 * Calculate employee payroll financials from raw earnings and deductions data.
 *
 * Approach B: Deductions = Government Only
 * - grossEarnings: Sum of all earnings (basic, OT, ND, holiday, restDay, pieceRate)
 * - timeDeductions: Late + Undertime + Absence (reduces gross, not a "deduction").
 *   Absence is the M1 deduct-down bucket (PRD_MonthlySalaryType): confirmed unpaid
 *   days for monthly-paid employees. Always 0 for daily employees (earn-up never
 *   emits it), so daily nets are byte-identical (B3).
 * - gross: grossEarnings - timeDeductions (what employee actually earned)
 * - totalDeductions: Government contributions only (SSS, PhilHealth, Pag-IBIG, Tax)
 * - totalLoans: loan installments (PRD_LoansManagement C8) — its own figure, NOT
 *   folded into totalDeductions, so every existing consumer of that field keeps
 *   its meaning; 0 for a line without the bucket (historical runs)
 * - net: gross - totalDeductions - totalLoans + netAdjustments
 *
 * @param {Object} employee - Employee object with earnings and deductions
 * @returns {Object} { gross, grossEarnings, timeDeductions, totalDeductions, totalWithheld, totalLoans, netAdjustments, net, breakdown }
 */
export function calculateEmployeeFinancials(employee) {
  const earnings = employee.earnings || {};
  const deductionsObj = employee.deductions || {};

  // === GROSS EARNINGS (before time deductions) ===
  const basic = earnings.basic || 0;
  const overtime = earnings.overtime || 0;
  const nightDiff = earnings.nightDiff || 0;
  const holiday = earnings.holiday || 0;
  const restDay = earnings.restDay || 0;
  const pieceRate = employee.pieceRate || 0;  // Note: at root level, not in earnings

  const grossEarnings = basic + overtime + nightDiff + holiday + restDay + pieceRate;

  // === TIME-BASED DEDUCTIONS (reduce gross, not "deductions") ===
  const late = deductionsObj.late || 0;
  const undertime = deductionsObj.undertime || 0;
  const absence = deductionsObj.absence || 0;
  const timeDeductions = late + undertime + absence;

  // === GROSS INCOME (after time deductions) ===
  const gross = grossEarnings - timeDeductions;

  // === GOVERNMENT DEDUCTIONS ONLY ===
  // Government contributions use per-month structure for cross-month support
  const sss = sumDeduction(deductionsObj.sss);
  const philhealth = sumDeduction(deductionsObj.philhealth);
  const pagibig = sumDeduction(deductionsObj.pagibig);
  const tax = sumDeduction(deductionsObj.tax);

  const totalDeductions = sss + philhealth + pagibig + tax;

  // === LOANS (the wizard's Loans step — PRD_LoansManagement C1/C8) ===
  const totalLoans = (Array.isArray(deductionsObj.loans) ? deductionsObj.loans : [])
    .reduce((sum, l) => sum + (l.amount || 0), 0);

  // === MANUAL ADJUSTMENTS (the Adjustments step) ===
  const manualEarnings = (earnings.adjustments || [])
    .reduce((sum, adj) => sum + (adj.amount || 0), 0);
  const manualDeductions = (deductionsObj.adjustments || [])
    .reduce((sum, adj) => sum + (adj.amount || 0), 0);
  const netAdjustments = manualEarnings - manualDeductions;

  // === NET PAY ===
  const net = gross - totalDeductions - totalLoans + netAdjustments;

  // === EVERYTHING WITHHELD (C2) — the individual payslip's "Total Deductions" ===
  // Derived, never persisted: `summary.totalDeductions` stays government-only (C3).
  const totalWithheld = totalDeductions + totalLoans + manualDeductions;

  return {
    // Summary totals (rounded to avoid floating-point display errors)
    gross: roundCurrency(gross),
    grossEarnings: roundCurrency(grossEarnings),  // Earnings before time deductions
    timeDeductions: roundCurrency(timeDeductions),  // Late + Undertime
    totalDeductions: roundCurrency(totalDeductions),  // Government only
    totalWithheld: roundCurrency(totalWithheld),  // C2 — payslip only
    totalLoans: roundCurrency(totalLoans),
    netAdjustments: roundCurrency(netAdjustments),
    net: roundCurrency(net),
    // Detailed breakdown for display
    breakdown: {
      earnings: {
        basic: roundCurrency(basic),
        overtime: roundCurrency(overtime),
        nightDiff: roundCurrency(nightDiff),
        holiday: roundCurrency(holiday),
        restDay: roundCurrency(restDay),
        pieceRate: roundCurrency(pieceRate)
      },
      timeDeductions: {
        late: roundCurrency(late),
        undertime: roundCurrency(undertime),
        absence: roundCurrency(absence)
      },
      deductions: {
        sss: roundCurrency(sss),
        philhealth: roundCurrency(philhealth),
        pagibig: roundCurrency(pagibig),
        tax: roundCurrency(tax)
      },
      adjustments: {
        manualEarnings: roundCurrency(manualEarnings),
        manualDeductions: roundCurrency(manualDeductions)
      }
    }
  };
}

/**
 * Format the quantity that produced a composition line — hours or days, or null
 * when the line carries no count. Hours and pesos belong on ONE line (C6c): splitting them into
 * parallel columns is what made the Gross Income table nine columns wide.
 */
function compositionQty(value, unit) {
  if (value === undefined || value === null) return null;
  if (unit === 'h') return `${Number(value).toFixed(2)}h`;
  const n = Number(value);
  return `${n} ${n === 1 ? 'day' : 'days'}`;
}

/**
 * An hour total for the period: the line's own total when it carries one, else the
 * sum of that field over the line's timeRecords. buildDtr's totals add only complete,
 * non-leave records; leave, absent and open records carry zero hours, so the sums agree.
 * computePayrollEligibility's projection sends the records but not the totals, so
 * live and saved server-compute lines take the second branch. null when the line
 * has neither.
 *
 * @param {Object} employee - a run line
 * @param {string} totalKey  - e.g. 'totalOvertimeHours'
 * @param {string} recordKey - e.g. 'overtimeHours'
 */
export function periodHours(employee, totalKey, recordKey) {
  const total = employee?.[totalKey];
  if (total !== undefined && total !== null) return total;
  const records = employee?.timeRecords;
  if (!Array.isArray(records) || records.length === 0) return null;
  return records.reduce((sum, r) => sum + (Number(r?.[recordKey]) || 0), 0);
}

// Round the magnitude, then apply the sign. This is NOT roundCurrency applied to a
// negative number — `roundCurrency(-38.125)` is -38.12, because Math.round sends a
// half toward +infinity. It matches what every other surface does, which is round
// the POSITIVE figure and negate it: -38.13. The payslip printed -38.12 against the
// Summary's and the CSV's -38.13 until this.
const toCents = (v) => {
  const n = v || 0;
  return n < 0 ? -Math.round(-n * 100) : Math.round(n * 100);
};

/**
 * PRD_PayrollFigureAlignment C6b — make a set of displayed amounts foot to a total.
 *
 * `roundCurrency` runs AFTER summing while the engine persists OT/ND/undertime
 * unrounded, so a sum of per-line rounded values is not the rounded sum. Each
 * amount is rounded to centavos and the leftover is then spread ONE CENTAVO PER
 * LINE, largest magnitude first.
 *
 * The per-line cap is the point. An unbounded "put it all on the biggest line"
 * made the footing tests unfalsifiable: a term missing from the model — the exact
 * defect C11 fixed on the Summary card — was absorbed into Basic Pay and every
 * test stayed green, printing the customer's original wrong figure under a
 * different name. With the cap, a genuine rounding gap (at most one centavo per
 * line, by construction) still closes, and anything larger stays visible as rows
 * that do not foot, which the invariant tests then catch.
 *
 * Used by BOTH row-producers — `buildPayComposition` here and `buildEarningsRows`
 * in payslipBuilders.
 *
 * @param {number[]} amounts   - raw (unrounded) line amounts, in pesos
 * @param {number}   targetCents - the total they must foot to, in whole centavos
 * @returns {number[]} per-line amounts in whole centavos; they sum to targetCents
 *                     whenever the gap is rounding-sized, and visibly do not when
 *                     it is not
 */
export function allocateRoundingResidue(amounts, targetCents) {
  const cents = amounts.map(toCents);
  let residue = targetCents - cents.reduce((sum, c) => sum + c, 0);
  if (residue === 0 || cents.length === 0) return cents;

  const step = residue > 0 ? 1 : -1;
  const order = cents
    .map((c, i) => i)
    .sort((a, b) => Math.abs(cents[b]) - Math.abs(cents[a]) || a - b);
  for (const i of order) {
    if (residue === 0) break;
    cents[i] += step;
    residue -= step;
  }
  return cents;
}

/**
 * Decompose an employee's Gross into the named lines a human can add up.
 *
 * PRD_PayrollFigureAlignment C6 — consumed by the Step-6 Summary's per-employee
 * Gross card and by the Gross Income screen (PRD_GrossIncomeRedesign). The payslip
 * does NOT use this: it keeps `buildEarningsRows`, which splits OT and ND by day
 * type and emits separate regular/special holiday rows (BL-086 C12, BL-046).
 *
 * C6b — THE LINES MUST FOOT. Every amount is rounded to centavos and the rounding
 * residue is allocated to the single largest-magnitude line (ties → the earliest,
 * since the order below is fixed), so the printed rows add to the printed Gross
 * exactly. Without this the rows differ from the total by a centavo — which is the
 * complaint this whole PRD exists to answer, with a smaller number.
 *
 * C10 — `pieceRate` and `late` have no writer in the app (the engine folds piece
 * pay into `earnings.basic`; `deductions.late` is only ever initialised to 0), so
 * they would render a permanent ₱0.00 on every panel. They are emitted ONLY when
 * non-zero: that keeps them off every real run exactly as C10 intends, while
 * guaranteeing that if a legacy line ever does carry one, the money appears as its
 * own line instead of being silently absorbed into the residue allocation above.
 *
 * @param {Object} employee - Employee object with earnings and deductions
 * @returns {Object} { lines: [{ key, label, qty, amount, kind, isZero }], gross }
 */
export function buildPayComposition(employee) {
  const fin = calculateEmployeeFinancials(employee);
  const e = fin.breakdown.earnings;
  const t = fin.breakdown.timeDeductions;
  const isMonthly = employee.employment?.salaryType === 'monthly';
  const restDays = (employee.earnings?.restDayDetails || []).length;
  const paidHolidays = (employee.earnings?.holidayDetails || [])
    .filter((h) => (h.holidayPay || 0) !== 0).length;

  const defs = [
    // Monthly is deduct-down: the base is a fixed amount, not a per-day sum, so a
    // day count is meaningless beside it and actively misleading at 0 days
    // ("₱15,000 for 0 days" reads like a daily employee earning nothing). Same
    // check the payslip's Basic Pay row already makes.
    { key: 'basic',     label: 'Basic pay',          kind: 'earning',       amount: e.basic,
      qty: isMonthly ? null : compositionQty(employee.daysWorked, 'd') },
    { key: 'pieceRate', label: 'Piece rate',         kind: 'earning',       amount: e.pieceRate, qty: null, onlyIfNonZero: true },
    { key: 'overtime',  label: 'Overtime',           kind: 'earning',       amount: e.overtime,  qty: compositionQty(periodHours(employee, 'totalOvertimeHours', 'overtimeHours'), 'h') },
    { key: 'nightDiff', label: 'Night differential', kind: 'earning',       amount: e.nightDiff, qty: compositionQty(periodHours(employee, 'totalNightDiffHours', 'nightDiffHours'), 'h') },
    // "Holiday pay", not "premium": for an unworked but entitled regular holiday
    // the engine pays a full day at rate 1, which is not a premium.
    { key: 'holiday',   label: 'Holiday pay',        kind: 'premium',       amount: e.holiday,   qty: paidHolidays ? compositionQty(paidHolidays, 'd') : null },
    { key: 'restDay',   label: 'Rest day premium',   kind: 'premium',       amount: e.restDay,   qty: restDays ? compositionQty(restDays, 'd') : null },
    { key: 'late',      label: 'Late',               kind: 'timeDeduction', amount: -t.late,     qty: null, onlyIfNonZero: true },
    { key: 'undertime', label: 'Undertime',          kind: 'timeDeduction', amount: -t.undertime, qty: compositionQty(periodHours(employee, 'totalUndertimeHours', 'undertimeHours'), 'h') },
    { key: 'absence',   label: 'Absence',            kind: 'timeDeduction', amount: -t.absence,  qty: null },
  ].filter((d) => !d.onlyIfNonZero || toCents(d.amount) !== 0);

  const cents = allocateRoundingResidue(defs.map((d) => d.amount), toCents(fin.gross));

  // PRD_ShiftRules A2 — the night hours that are also OT become their own line,
  // split AFTER the residue step so the pair adds to the night figure printed before
  // the split and every other line is unchanged.
  const ndRows = employee.earnings?.nightDiffDetails || [];
  const ndOtHours = ndRows.reduce((sum, r) => sum + (r.ndOtHours || 0), 0);
  const ndOtCents = toCents(ndRows.reduce((sum, r) => sum + (r.ndOtPay || 0), 0));

  const lines = [];
  defs.forEach((d, i) => {
    const line = { key: d.key, label: d.label, qty: d.qty, kind: d.kind, cents: cents[i] };
    if (d.key === 'nightDiff' && ndOtCents !== 0) {
      const ndHours = periodHours(employee, 'totalNightDiffHours', 'nightDiffHours');
      line.cents -= ndOtCents;
      line.qty = ndHours === null || ndHours === undefined ? null :
        compositionQty(Math.round((ndHours - ndOtHours) * 100) / 100, 'h');
      lines.push(line, {
        key: 'nightDiffOt', label: 'Night differential on OT', kind: 'earning',
        qty: compositionQty(ndOtHours, 'h'), cents: ndOtCents,
      });
      return;
    }
    lines.push(line);
  });

  return {
    lines: lines.map(({ cents: c, ...line }) => ({ ...line, amount: c / 100, isZero: c === 0 })),
    gross: fin.gross,
  };
}

/**
 * Calculate totals for an array of employees (for summary cards).
 * @param {Array} employees - Array of employee objects
 * @returns {Object} { totalGross, totalDeductions, totalNet, employeeCount }
 */
export function calculatePayrollTotals(employees) {
  const result = {
    totalGross: 0,
    totalDeductions: 0,
    totalNet: 0,
    employeeCount: employees?.length || 0
  };

  (employees || []).forEach(emp => {
    const { gross, totalDeductions, net } = calculateEmployeeFinancials(emp);
    result.totalGross += gross;
    result.totalDeductions += totalDeductions;
    result.totalNet += net;
  });

  return result;
}

/**
 * Calculate full payroll summary from employees array.
 * This is the single source of truth for all payroll summary displays.
 * @param {Array} employees - Array of employee objects from payroll run
 * @returns {Object} Complete summary with totals and deduction breakdown
 */
export function calculatePayrollSummary(employees = []) {
  const summary = {
    totalEmployees: employees.length,
    totalGross: 0,
    totalDeductions: 0,
    totalLoans: 0, // PRD_LoansManagement C8 — readers of a STORED summary default it to 0
    totalAdjustments: 0,
    totalNet: 0,
    deductionBreakdown: { sss: 0, philhealth: 0, pagibig: 0, tax: 0 }
  };

  employees.forEach(emp => {
    const financials = calculateEmployeeFinancials(emp);
    summary.totalGross += financials.gross;
    summary.totalDeductions += financials.totalDeductions;
    summary.totalLoans += financials.totalLoans;
    summary.totalAdjustments += financials.netAdjustments;
    summary.totalNet += financials.net;

    // Aggregate deduction breakdown (supports per-month structure)
    summary.deductionBreakdown.sss += sumDeduction(emp.deductions?.sss);
    summary.deductionBreakdown.philhealth += sumDeduction(emp.deductions?.philhealth);
    summary.deductionBreakdown.pagibig += sumDeduction(emp.deductions?.pagibig);
    summary.deductionBreakdown.tax += sumDeduction(emp.deductions?.tax);
  });

  return summary;
}
